"use client";

import { useEffect, useRef } from "react";

const CYCLE_MS = 22000;
const SONG_TOTAL_S = 212; // 3:32

// Pre-compute bar rect attributes with bounded precision so SSR and CSR
// produce byte-identical SVG attribute strings (Math.sin/cos can differ in
// the last bit between Node and browser engines, causing hydration errors).
const BAR_RECTS = Array.from({ length: 180 }, (_, i) => {
  const t = i / 180;
  const a =
    Math.sin(t * 6 * Math.PI) * 0.5 +
    Math.sin(t * 13.7 * Math.PI) * 0.3 +
    Math.cos(t * 22.4 * Math.PI) * 0.2 +
    Math.sin(t * 41.1 * Math.PI) * 0.12;
  const env = 0.65 + 0.35 * Math.sin(t * Math.PI);
  const amp = Math.max(0.1, Math.min(1, Math.abs(a) * env * 0.95 + 0.12));
  const x = (i / 180) * 800;
  const w = 800 / 180 - 1.2;
  const h = amp * 32;
  const y = 18 - h / 2;
  return {
    x: x.toFixed(3),
    y: y.toFixed(3),
    w: w.toFixed(3),
    h: h.toFixed(3),
  };
});

const TUNING = ["D", "B", "G", "D", "G", "D"];

type TabNote = { string: number; fret: number };
type TabEntry = {
  notes: TabNote[];
  chord?: string;
  pm?: boolean;
  hammer?: boolean;   // arc to next beat with "h"
  slide?: boolean;    // diagonal slash to next beat
  vibrato?: boolean;  // ~ marker near top note
  measureStart?: number; // bar number to print at the start of this beat
};

// Parsed from "The Rolling Stones - Start Me Up.gp5" — Keith Richards.
// 48 beats split into 4 rows of 12, with a few realistic technique
// annotations layered on (palm mute, hammer-on, slide, vibrato) common
// to the riff's actual performance.
const ROWS: TabEntry[][] = [
  [
    { notes: [], measureStart: 1 },
    { notes: [] },
    { notes: [] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }, { string: 5, fret: 5 }], chord: "G", pm: true },
    { notes: [] },
    { notes: [], measureStart: 2 },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }], chord: "C", pm: true },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }], pm: true },
    { notes: [] },
    { notes: [] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }, { string: 5, fret: 5 }], chord: "G" },
    { notes: [] },
  ],
  [
    { notes: [], measureStart: 3 },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }, { string: 5, fret: 5 }], chord: "C" },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }, { string: 5, fret: 5 }], hammer: true },
    { notes: [{ string: 2, fret: 5 }, { string: 4, fret: 5 }] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }, { string: 5, fret: 5 }], chord: "G", vibrato: true },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }, { string: 5, fret: 5 }], chord: "C" },
    { notes: [{ string: 3, fret: 0 }, { string: 4, fret: 0 }, { string: 5, fret: 0 }] },
    { notes: [], measureStart: 4 },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 3 }, { string: 5, fret: 3 }], chord: "F" },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 5 }, { string: 5, fret: 3 }] },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 3 }, { string: 5, fret: 3 }] },
  ],
  [
    { notes: [{ string: 5, fret: 3 }] },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 5 }], slide: true },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 3 }, { string: 5, fret: 3 }] },
    { notes: [], measureStart: 5 },
    { notes: [{ string: 4, fret: 3 }, { string: 5, fret: 3 }], pm: true },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 5 }, { string: 5, fret: 3 }], pm: true },
    { notes: [{ string: 4, fret: 3 }, { string: 5, fret: 3 }], pm: true },
    { notes: [{ string: 2, fret: 3 }, { string: 3, fret: 3 }, { string: 4, fret: 3 }, { string: 5, fret: 3 }], chord: "F" },
    { notes: [{ string: 3, fret: 3 }, { string: 4, fret: 3 }] },
    { notes: [{ string: 2, fret: 3 }, { string: 3, fret: 3 }, { string: 4, fret: 3 }, { string: 5, fret: 3 }] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }, { string: 5, fret: 5 }], chord: "G" },
    { notes: [] },
  ],
  [
    { notes: [], measureStart: 6 },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }], chord: "C" },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }, { string: 5, fret: 5 }], hammer: true },
    { notes: [] },
    { notes: [] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }, { string: 5, fret: 5 }], chord: "G" },
    { notes: [] },
    { notes: [], measureStart: 7 },
    { notes: [{ string: 1, fret: 5 }, { string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }], chord: "C" },
    { notes: [{ string: 2, fret: 6 }, { string: 3, fret: 5 }, { string: 4, fret: 7 }, { string: 5, fret: 5 }] },
    { notes: [{ string: 2, fret: 5 }, { string: 3, fret: 5 }, { string: 4, fret: 5 }, { string: 5, fret: 5 }], chord: "G", vibrato: true },
    { notes: [] },
  ],
];

const NUM_ROWS = ROWS.length;
const ENTRIES_PER_ROW = ROWS[0].length;
const TOTAL_ENTRIES = NUM_ROWS * ENTRIES_PER_ROW;

const LEFT = 32;
const RIGHT = 14;
const TOP = 22;
const STRING_SPACING = 12;
const ENTRY_SPACING = 36;
const NUM_STRINGS = 6;
const STAFF_H = (NUM_STRINGS - 1) * STRING_SPACING;
const ROW_GAP = 26;
const SVG_W = LEFT + ENTRIES_PER_ROW * ENTRY_SPACING + RIGHT;
const SVG_H = TOP + NUM_ROWS * STAFF_H + (NUM_ROWS - 1) * ROW_GAP + 18;

const TAB_BG = "#15120F";
const STRING_COLOR = "#3F3A33";
const FRET_TEXT = "#E8E4DF";
const TUNING_TEXT = "#7A746B";
const CHORD_TEXT = "#F59E0B";
const META_TEXT = "#9A958B";
const TECH_COLOR = "#FCD34D";
const PLAYHEAD = "rgba(245, 158, 11, 0.18)";
const PLAYHEAD_EDGE = "rgba(245, 158, 11, 0.55)";

const rowTopY = (row: number) => TOP + row * (STAFF_H + ROW_GAP);
const stringY = (row: number, s: number) => rowTopY(row) + (s - 1) * STRING_SPACING;
const entryX = (col: number) => LEFT + col * ENTRY_SPACING + ENTRY_SPACING / 2;

type ChordShape = {
  name: string;
  fretLabel?: string;
  barre?: { fromString: number; toString: number; fret: number };
  fingers: { string: number; fret: number; finger: number }[];
  muted?: number[];
};

const CHORDS: ChordShape[] = [
  { name: "G", fingers: [{ string: 6, fret: 3, finger: 2 }, { string: 5, fret: 2, finger: 1 }, { string: 1, fret: 3, finger: 3 }] },
  { name: "C", fingers: [{ string: 5, fret: 3, finger: 3 }, { string: 4, fret: 2, finger: 2 }, { string: 2, fret: 1, finger: 1 }], muted: [6] },
  { name: "F", fretLabel: "1f", barre: { fromString: 1, toString: 6, fret: 1 }, fingers: [{ string: 5, fret: 3, finger: 3 }, { string: 4, fret: 3, finger: 4 }, { string: 3, fret: 2, finger: 2 }] },
  { name: "G5", fretLabel: "3f", fingers: [{ string: 6, fret: 1, finger: 1 }, { string: 5, fret: 3, finger: 3 }, { string: 4, fret: 3, finger: 4 }], muted: [1, 2, 3] },
  { name: "Cadd9", fingers: [{ string: 5, fret: 3, finger: 3 }, { string: 4, fret: 2, finger: 2 }, { string: 2, fret: 3, finger: 4 }], muted: [6] },
  { name: "D", fingers: [{ string: 3, fret: 2, finger: 1 }, { string: 1, fret: 2, finger: 2 }, { string: 2, fret: 3, finger: 3 }], muted: [6, 5] },
];

function ChordDiagram({ shape }: { shape: ChordShape }) {
  const W = 56;
  const H = 64;
  const padX = 8;
  const padTop = 14;
  const gridW = W - padX * 2;
  const gridH = H - padTop - 6;
  const stringX = (s: number) => padX + ((6 - s) / 5) * gridW;
  const fretY = (f: number) => padTop + (f / 4) * gridH;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-[11px] font-semibold text-[#E8E4DF] tracking-wide">{shape.name}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" aria-hidden="true">
        <line x1={padX} y1={padTop} x2={W - padX} y2={padTop} stroke="#E8E4DF" strokeWidth={shape.fretLabel ? 0.5 : 1.5} />
        {shape.fretLabel && (
          <text x={W - 2} y={padTop + 4} textAnchor="end" fill={META_TEXT} fontSize={7} fontFamily="ui-monospace, monospace">
            {shape.fretLabel}
          </text>
        )}
        {[1, 2, 3, 4, 5, 6].map((s) => (
          <line key={s} x1={stringX(s)} y1={padTop} x2={stringX(s)} y2={padTop + gridH} stroke="#3F3A33" strokeWidth={0.5} />
        ))}
        {[1, 2, 3, 4].map((f) => (
          <line key={f} x1={padX} y1={fretY(f)} x2={W - padX} y2={fretY(f)} stroke="#3F3A33" strokeWidth={0.5} />
        ))}
        {shape.muted?.map((s) => (
          <text key={`m${s}`} x={stringX(s)} y={padTop - 3} textAnchor="middle" fill={META_TEXT} fontSize={6} fontFamily="ui-monospace, monospace">
            ×
          </text>
        ))}
        {shape.barre && (() => {
          const x1 = stringX(shape.barre.fromString);
          const x2 = stringX(shape.barre.toString);
          return (
            <rect
              x={Math.min(x1, x2) - 2}
              y={fretY(shape.barre.fret) - (gridH / 4) / 2 - 2}
              width={Math.abs(x2 - x1) + 4}
              height={4}
              rx={2}
              fill="#F59E0B"
              opacity={0.85}
            />
          );
        })()}
        {shape.fingers.map((f, i) => (
          <g key={i}>
            <circle cx={stringX(f.string)} cy={fretY(f.fret) - (gridH / 4) / 2} r={3.6} fill="#F59E0B" />
            <text
              x={stringX(f.string)}
              y={fretY(f.fret) - (gridH / 4) / 2 + 2}
              textAnchor="middle"
              fill="#15120F"
              fontSize={5.5}
              fontWeight={700}
              fontFamily="system-ui, sans-serif"
            >
              {f.finger}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// Compute palm-mute spans (consecutive pm=true beats in a row).
function pmSpans(row: TabEntry[]): { start: number; end: number }[] {
  const spans: { start: number; end: number }[] = [];
  let cur: { start: number; end: number } | null = null;
  row.forEach((entry, i) => {
    if (entry.pm) {
      if (!cur) cur = { start: i, end: i };
      else cur.end = i;
    } else if (cur) {
      spans.push(cur);
      cur = null;
    }
  });
  if (cur) spans.push(cur);
  return spans;
}

// Highest string number used in an entry (string 1 = top), for placing markers.
function topStringOf(entry: TabEntry): number {
  if (!entry.notes.length) return 1;
  return entry.notes.reduce((min, n) => Math.min(min, n.string), 6);
}

export function AnimatedHeroPreview() {
  const rootRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const barsRef = useRef<SVGGElement>(null);
  const tabPlayheadRef = useRef<SVGRectElement>(null);
  const tabPlayheadEdgeRef = useRef<SVGLineElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const start = performance.now();
    let lastPlayedIndex = 0;

    const tick = (now: number) => {
      const t = ((now - start) % CYCLE_MS) / CYCLE_MS;

      if (playheadRef.current) {
        playheadRef.current.style.left = `${t * 100}%`;
      }
      if (timeRef.current) {
        const sec = Math.floor(t * SONG_TOTAL_S);
        const m = Math.floor(sec / 60);
        const s = (sec % 60).toString().padStart(2, "0");
        timeRef.current.textContent = `${m}:${s} / 3:32`;
      }
      if (barsRef.current && barsRef.current.children.length > 0) {
        const bars = barsRef.current.children;
        const len = bars.length;
        const playedIndex = Math.floor(t * len);
        // Clamp the prior index to the current children length. A stale
        // lastPlayedIndex from a previous render (or a transiently emptied
        // <g>) would otherwise drive the loop past the end of `bars` and
        // crash on `undefined.setAttribute`.
        const prevIndex = Math.min(lastPlayedIndex, len);
        if (playedIndex > prevIndex) {
          for (let i = prevIndex; i < playedIndex; i++) {
            (bars[i] as SVGRectElement | undefined)?.setAttribute("fill", "#F59E0B");
          }
        } else if (playedIndex < prevIndex) {
          // Cycle wrap: reset bars that were amber back to gray.
          for (let i = playedIndex; i < prevIndex; i++) {
            (bars[i] as SVGRectElement | undefined)?.setAttribute("fill", "#3F3A33");
          }
        }
        lastPlayedIndex = playedIndex;
      }

      const globalIdx = Math.min(TOTAL_ENTRIES - 1, Math.floor(t * TOTAL_ENTRIES));
      const row = Math.floor(globalIdx / ENTRIES_PER_ROW);
      const col = globalIdx % ENTRIES_PER_ROW;
      const colX = LEFT + col * ENTRY_SPACING;
      const colY = rowTopY(row) - 12;

      if (tabPlayheadRef.current) {
        tabPlayheadRef.current.setAttribute("x", String(colX));
        tabPlayheadRef.current.setAttribute("y", String(colY));
      }
      if (tabPlayheadEdgeRef.current) {
        const edgeX = colX + ENTRY_SPACING / 2;
        tabPlayheadEdgeRef.current.setAttribute("x1", String(edgeX));
        tabPlayheadEdgeRef.current.setAttribute("x2", String(edgeX));
        tabPlayheadEdgeRef.current.setAttribute("y1", String(colY));
        tabPlayheadEdgeRef.current.setAttribute("y2", String(colY + STAFF_H + 18));
      }

      raf = requestAnimationFrame(tick);
    };

    let isVisible = true;
    let isTabActive = !document.hidden;

    const startLoop = () => {
      if (raf || !isVisible || !isTabActive) return;
      raf = requestAnimationFrame(tick);
    };
    const stopLoop = () => {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) startLoop();
        else stopLoop();
      },
      { threshold: 0 },
    );
    if (rootRef.current) io.observe(rootRef.current);

    const onVisibility = () => {
      isTabActive = !document.hidden;
      if (isTabActive) startLoop();
      else stopLoop();
    };
    document.addEventListener("visibilitychange", onVisibility);

    startLoop();

    return () => {
      stopLoop();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div ref={rootRef} className="rounded-2xl border border-[#2A2825] bg-[#0F0E0C] overflow-hidden shadow-2xl shadow-[#F59E0B]/10">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#2A2825]">
        <div className="min-w-0">
          <div className="text-[#F59E0B] font-bold text-[13px] tracking-tight">audio2guitar</div>
          <div className="mt-1.5 text-[#E8E4DF] font-bold tracking-wide truncate">
            ROLLING STONES &mdash; START ME UP
          </div>
          <div className="text-[#9A958B] text-[11px] mt-0.5">
            Tuning: Open G &nbsp;|&nbsp; Key: G &nbsp;|&nbsp; Tempo: 125 BPM
          </div>
        </div>
        <div className="flex rounded-md border border-[#2A2825] overflow-hidden text-[11px] shrink-0">
          <span className="px-3 py-1.5 bg-[#F59E0B] text-[#121110] font-semibold">Tab</span>
          <span className="px-3 py-1.5 text-[#9A958B]">Chords</span>
        </div>
      </div>

      {/* Transport */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[#2A2825]">
        <div className="h-8 w-8 rounded-full bg-[#F59E0B] flex items-center justify-center shrink-0">
          <svg viewBox="0 0 12 12" className="h-3 w-3 fill-[#121110]" aria-hidden="true">
            <polygon points="3,2 10,6 3,10" />
          </svg>
        </div>
        <div className="flex-1 relative h-9">
          <svg
            viewBox="0 0 800 36"
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <g ref={barsRef}>
              {BAR_RECTS.map((b, i) => (
                <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} fill="#3F3A33" rx={0.8} />
              ))}
            </g>
          </svg>
          <div
            ref={playheadRef}
            className="pointer-events-none absolute -top-1 -bottom-1 w-px bg-[#FCD34D]"
            style={{ left: "0%", boxShadow: "0 0 10px rgba(252, 211, 77, 0.9)" }}
          >
            <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rounded-full bg-[#FCD34D]" />
          </div>
        </div>
        <span ref={timeRef} className="text-[11px] text-[#9A958B] tabular-nums shrink-0 w-20 text-right">
          0:00 / 3:32
        </span>
      </div>

      {/* Tab + chord sidebar */}
      <div className="flex items-stretch">
        <div className="flex-1 min-w-0 bg-[#15120F] p-3">
          <svg
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            className="w-full h-auto block"
            aria-label="Start Me Up intro tab notation"
          >
            <text x={6} y={12} fill={META_TEXT} fontSize={9} fontFamily="ui-monospace, monospace">
              ♩ = 125
            </text>

            {/* Column playhead */}
            <rect
              ref={tabPlayheadRef}
              x={LEFT}
              y={rowTopY(0) - 12}
              width={ENTRY_SPACING}
              height={STAFF_H + 18}
              fill={PLAYHEAD}
              style={{ transition: "x 200ms cubic-bezier(0.22, 1, 0.36, 1), y 220ms ease-out" }}
            />
            <line
              ref={tabPlayheadEdgeRef}
              x1={LEFT + ENTRY_SPACING / 2}
              y1={rowTopY(0) - 12}
              x2={LEFT + ENTRY_SPACING / 2}
              y2={rowTopY(0) + STAFF_H + 6}
              stroke={PLAYHEAD_EDGE}
              strokeWidth={1.2}
              style={{ transition: "x1 200ms ease-out, x2 200ms ease-out, y1 220ms ease-out, y2 220ms ease-out" }}
            />

            {ROWS.map((row, ri) => {
              const yTop = rowTopY(ri);
              const yBot = yTop + STAFF_H;
              return (
                <g key={ri}>
                  {/* Bar number marker */}
                  {row[0]?.measureStart !== undefined && (
                    <text x={6} y={yTop + 3} fill={META_TEXT} fontSize={8} fontFamily="ui-monospace, monospace">
                      {row[0].measureStart}
                    </text>
                  )}

                  {/* String lines */}
                  {Array.from({ length: NUM_STRINGS }, (_, i) => {
                    const y = stringY(ri, i + 1);
                    return (
                      <g key={i}>
                        <line
                          x1={LEFT - 6}
                          y1={y}
                          x2={SVG_W - RIGHT + 6}
                          y2={y}
                          stroke={STRING_COLOR}
                          strokeWidth={0.7}
                        />
                        <text
                          x={LEFT - 10}
                          y={y + 2.8}
                          textAnchor="end"
                          fill={TUNING_TEXT}
                          fontSize={8}
                          fontFamily="ui-monospace, monospace"
                        >
                          {TUNING[i]}
                        </text>
                      </g>
                    );
                  })}

                  {/* Side barlines */}
                  <line x1={LEFT - 6} y1={yTop} x2={LEFT - 6} y2={yBot} stroke="#4A443B" strokeWidth={1.1} />
                  <line x1={SVG_W - RIGHT + 6} y1={yTop} x2={SVG_W - RIGHT + 6} y2={yBot} stroke="#4A443B" strokeWidth={1.1} />

                  {/* Internal barlines at each measure boundary */}
                  {row.map((entry, ci) =>
                    entry.measureStart !== undefined && ci > 0 ? (
                      <line
                        key={`bar${ci}`}
                        x1={entryX(ci) - ENTRY_SPACING / 2}
                        y1={yTop}
                        x2={entryX(ci) - ENTRY_SPACING / 2}
                        y2={yBot}
                        stroke="#3A352E"
                        strokeWidth={0.8}
                      />
                    ) : null,
                  )}

                  {/* P.M. brackets */}
                  {pmSpans(row).map((span, si) => {
                    const x1 = entryX(span.start) - 8;
                    const x2 = entryX(span.end) + 8;
                    const yLine = yTop - 4;
                    return (
                      <g key={`pm${si}`}>
                        <text
                          x={x1}
                          y={yLine - 1}
                          fill={META_TEXT}
                          fontSize={8}
                          fontFamily="ui-monospace, monospace"
                        >
                          P.M.
                        </text>
                        <line
                          x1={x1 + 16}
                          y1={yLine}
                          x2={x2}
                          y2={yLine}
                          stroke={META_TEXT}
                          strokeWidth={0.6}
                          strokeDasharray="2,2"
                        />
                        <line x1={x2} y1={yLine - 2} x2={x2} y2={yLine + 2} stroke={META_TEXT} strokeWidth={0.6} />
                      </g>
                    );
                  })}

                  {/* Chord labels */}
                  {row.map((entry, ci) =>
                    entry.chord ? (
                      <text
                        key={`c${ci}`}
                        x={entryX(ci)}
                        y={yTop - 14}
                        textAnchor="middle"
                        fill={CHORD_TEXT}
                        fontSize={9}
                        fontWeight={700}
                        fontFamily="system-ui, sans-serif"
                      >
                        {entry.chord}
                      </text>
                    ) : null,
                  )}

                  {/* Notes */}
                  {row.map((entry, ci) => {
                    const x = entryX(ci);
                    return (
                      <g key={`n${ci}`}>
                        {entry.notes.map((note, ni) => {
                          const y = stringY(ri, note.string);
                          const text = String(note.fret);
                          const w = text.length > 1 ? 13 : 9;
                          return (
                            <g key={ni}>
                              <rect x={x - w / 2} y={y - 6.5} width={w} height={13} fill={TAB_BG} rx={1} />
                              <text
                                x={x}
                                y={y + 3.2}
                                textAnchor="middle"
                                fill={FRET_TEXT}
                                fontSize={10}
                                fontWeight={600}
                                fontFamily="system-ui, sans-serif"
                              >
                                {text}
                              </text>
                            </g>
                          );
                        })}

                        {/* Hammer-on arc to next entry */}
                        {entry.hammer && ci < row.length - 1 && entry.notes.length > 0 && (
                          <g>
                            <path
                              d={`M ${x + 4} ${stringY(ri, topStringOf(entry)) - 4} Q ${x + ENTRY_SPACING / 2} ${stringY(ri, topStringOf(entry)) - 12} ${x + ENTRY_SPACING - 4} ${stringY(ri, topStringOf(entry)) - 4}`}
                              fill="none"
                              stroke={TECH_COLOR}
                              strokeWidth={0.8}
                            />
                            <text
                              x={x + ENTRY_SPACING / 2}
                              y={stringY(ri, topStringOf(entry)) - 13}
                              textAnchor="middle"
                              fill={TECH_COLOR}
                              fontSize={8}
                              fontWeight={700}
                              fontFamily="system-ui, sans-serif"
                            >
                              h
                            </text>
                          </g>
                        )}

                        {/* Slide marker */}
                        {entry.slide && entry.notes.length > 0 && (
                          <text
                            x={x + 9}
                            y={stringY(ri, topStringOf(entry)) + 3}
                            fill={TECH_COLOR}
                            fontSize={11}
                            fontWeight={700}
                            fontFamily="system-ui, sans-serif"
                          >
                            /
                          </text>
                        )}

                        {/* Vibrato squiggle */}
                        {entry.vibrato && entry.notes.length > 0 && (
                          <text
                            x={x}
                            y={stringY(ri, topStringOf(entry)) - 9}
                            textAnchor="middle"
                            fill={TECH_COLOR}
                            fontSize={10}
                            fontFamily="system-ui, sans-serif"
                          >
                            ~
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>

        <div className="shrink-0 w-36 sm:w-40 bg-[#0F0E0C] border-l border-[#2A2825] p-3 flex flex-col">
          <div className="text-[10px] font-bold tracking-[0.18em] text-[#9A958B] mb-2.5">CHORDS</div>
          <div className="grid grid-cols-2 gap-2 flex-1 content-around">
            {CHORDS.map((c) => (
              <ChordDiagram key={c.name} shape={c} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
